import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, GraduationCap, Building2, CreditCard, ArrowRight, Loader2,
  RefreshCw, Sparkles, Crown, AlertCircle, Gift,
  UserPlus, Upload, BookOpen, ClipboardList,
  Ribbon, Bell, Settings, Link2, CalendarCheck,
} from 'lucide-react'
import { dashboardApi, subscriptionApi } from '../api/services.js'
import { useSelector } from 'react-redux'
import PageHeader from '../components/PageHeader.jsx'

const formatShortDate = (value = new Date()) =>
  new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).format(value)

// ── Subscription banner ────────────────────────────────────────────────────────
function SubscriptionBanner({ sub, onNavigate }) {
  if (!sub) return null
  const daysLeft = Math.max(0, Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / 86400000))
  const isExpired = new Date(sub.endDate) < new Date()
  const isTrial   = sub.plan === 'trial'
  const isExpiring = daysLeft <= 14

  if (!isExpired && !isTrial && !isExpiring) return null

  const config = isExpired
    ? { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-700',   btn: 'bg-red-600',   Icon: AlertCircle, label: 'Subscription Expired', body: 'Renew now to restore full access for your school.', cta: 'Renew Now' }
    : isTrial
    ? { bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-700',  btn: 'bg-blue-600',  Icon: Gift,        label: `Free Trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`, body: 'Upgrade to a paid plan to keep access after your trial ends.', cta: 'Upgrade' }
    : { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', btn: 'bg-amber-500', Icon: Crown,       label: `Plan expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`, body: 'Renew before it expires to avoid disruption.', cta: 'View Plan' }

  const { Icon } = config
  return (
    <div
      className={`rounded-2xl border ${config.bg} ${config.border} p-4 flex items-center gap-4 cursor-pointer hover:opacity-90 transition-opacity`}
      onClick={() => onNavigate('/subscription')}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/60">
        <Icon className={`w-5 h-5 ${config.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${config.text}`}>{config.label}</p>
        <p className={`text-xs mt-0.5 ${config.text} opacity-80`}>{config.body}</p>
      </div>
      <div className={`flex-shrink-0 text-xs font-bold text-white px-3 py-2 rounded-xl ${config.btn}`}>
        {config.cta}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { user } = useSelector((s) => s.auth)
  const [stats, setStats]           = useState(null)
  const [feeSummary, setFeeSummary] = useState(null)
  const [activity, setActivity]     = useState([])
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading]       = useState(true)

  const fetchAll = async () => {
    try {
      setLoading(true)
      const [studentsRes, teachersRes, classesRes, feeSummaryRes, activityRes, parentsRes, subRes] =
        await Promise.allSettled([
          dashboardApi.getStudentCount(),
          dashboardApi.getTeacherCount(),
          dashboardApi.getClassCount(),
          dashboardApi.getFeeSummary(),
          dashboardApi.getActivity(),
          dashboardApi.getParentCount(),
          subscriptionApi.get().catch(() => null),
        ])

      // All students array — derive every student-related count from this single source
      const studentsArr = studentsRes.status === 'fulfilled'
        ? (Array.isArray(studentsRes.value) ? studentsRes.value : studentsRes.value?.users ?? [])
        : []
      const totalStudentsFromApi = studentsRes.status === 'fulfilled'
        ? (Array.isArray(studentsRes.value) ? studentsRes.value.length : studentsRes.value?.total ?? 0)
        : 0

      const activeStudents = studentsArr.filter((u) => u.studentStatus !== 'completed').length
      const alumni         = studentsArr.filter((u) => u.studentStatus === 'completed').length
      const boys           = studentsArr.filter((u) => u.gender === 'male').length
      const girls          = studentsArr.filter((u) => u.gender === 'female').length

      const teachers = teachersRes.status === 'fulfilled'
        ? (Array.isArray(teachersRes.value) ? teachersRes.value.length : teachersRes.value?.total ?? 0)
        : 0
      const classes = classesRes.status === 'fulfilled'
        ? (Array.isArray(classesRes.value) ? classesRes.value.length : classesRes.value?.total ?? 0)
        : 0
      const fee = feeSummaryRes.status === 'fulfilled' ? feeSummaryRes.value : null

      const parentsArr = parentsRes.status === 'fulfilled'
        ? (Array.isArray(parentsRes.value) ? parentsRes.value : parentsRes.value?.users ?? [])
        : []
      const parents = parentsArr.length || (parentsRes.status === 'fulfilled' ? (parentsRes.value?.total ?? 0) : 0)

      setStats({
        students: activeStudents || totalStudentsFromApi,
        teachers,
        classes,
        pendingFees:   fee?.totalPending   ?? 0,
        collectedFees: fee?.totalCollected ?? 0,
        parents,
        alumni,
        boys,
        girls,
      })
      if (fee) setFeeSummary(fee)

      if (activityRes.status === 'fulfilled') {
        const list = Array.isArray(activityRes.value) ? activityRes.value : activityRes.value?.notifications ?? []
        setActivity(list.slice(0, 5))
      }

      if (subRes.status === 'fulfilled' && subRes.value) {
        const s = subRes.value
        setSubscription(s?.subscription ?? s?.data?.subscription ?? s)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [])

  const primaryStats = [
    { label: 'Total Students', value: stats?.students, icon: Users,         color: 'bg-blue-50   text-blue-600',   link: '/students' },
    { label: 'Teachers',       value: stats?.teachers, icon: GraduationCap, color: 'bg-green-50  text-green-600',  link: '/teachers' },
    { label: 'Classes',        value: stats?.classes,  icon: Building2,     color: 'bg-purple-50 text-purple-600', link: '/classes' },
    { label: 'Pending Fees',
      value: stats?.pendingFees ? `₹${(stats.pendingFees / 100000).toFixed(1)}L` : '₹0',
      icon: CreditCard, color: 'bg-orange-50 text-orange-600', link: '/fees/defaulters' },
  ]

  const secondaryStats = [
    { label: 'Parents', value: stats?.parents, icon: Users,  color: 'bg-amber-50 text-amber-600', link: '/parent-student-links' },
    { label: 'Alumni',  value: stats?.alumni,  icon: Ribbon, color: 'bg-rose-50  text-rose-600',  link: '/alumni' },
    { label: 'Boys',    value: stats?.boys,    icon: Users,  color: 'bg-sky-50   text-sky-600',   link: '/students' },
    { label: 'Girls',   value: stats?.girls,   icon: Users,  color: 'bg-pink-50  text-pink-600',  link: '/students' },
  ]

  const quickActions = [
    { label: 'Add User',           link: '/users',               icon: UserPlus },
    { label: 'Bulk Upload',        link: '/bulk-upload',         icon: Upload },
    { label: 'Mark Attendance',    link: '/attendance',          icon: CalendarCheck },
    { label: 'Collect Fee',        link: '/fees/collection',     icon: CreditCard },
    { label: 'Fee Management',     link: '/fee-management',      icon: Sparkles },
    { label: 'Send Notification',  link: '/notifications',       icon: Bell },
    { label: 'Exam Results',       link: '/exams',               icon: ClipboardList },
    { label: 'Subjects',           link: '/subjects',            icon: BookOpen },
    { label: 'Parent Links',       link: '/parent-student-links', icon: Link2 },
    { label: 'School Settings',    link: '/school-settings',     icon: Settings },
  ]

  const activityColors = ['bg-green-500', 'bg-yellow-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500']

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        subtitle="Start the day with a clear view of your school, then jump straight into the tasks that need attention."
        chips={[
          formatShortDate(),
          stats ? `${stats.students || 0} students` : 'Loading school data',
          stats?.pendingFees ? `₹${(stats.pendingFees / 100000).toFixed(1)}L pending` : 'Fees up to date',
        ]}
        actions={
          <button onClick={fetchAll} className="btn-secondary">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />

      {/* Subscription banner */}
      {!loading && subscription && (
        <SubscriptionBanner sub={subscription} onNavigate={navigate} />
      )}

      {/* Hero card + quick actions */}
      <div className="soft-panel p-5 lg:p-6 bg-gradient-to-r from-slate-900 via-slate-900 to-primary-900 text-white overflow-hidden relative rounded-2xl">
        <div className="absolute inset-y-0 right-0 w-56 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18),transparent_62%)] pointer-events-none" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/85">
              <Sparkles className="w-3.5 h-3.5" /> Admin control center
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold tracking-tight mt-4">
              Keep daily operations moving with fewer clicks.
            </h3>
            <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-xl">
              Review attendance, pending fees, admissions, and communications — all from one place.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2 lg:min-w-[360px]">
            {quickActions.map((a) => {
              const Icon = a.icon
              return (
                <button
                  key={a.label}
                  onClick={() => navigate(a.link)}
                  className="rounded-2xl border border-white/10 bg-white/10 hover:bg-white/20 text-xs font-semibold px-3 py-3 transition-colors flex items-center gap-2 backdrop-blur-sm text-left"
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {a.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Primary stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {primaryStats.map((c) => {
          const Icon = c.icon
          return (
            <div key={c.label} onClick={() => navigate(c.link)} className="card p-5 cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl ${c.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </div>
              <p className="text-2xl font-extrabold text-gray-900 mb-1">
                {loading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (c.value ?? '—')}
              </p>
              <p className="text-sm text-gray-500">{c.label}</p>
            </div>
          )
        })}
      </div>

      {/* Secondary stats — parents, alumni, boys, girls */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {secondaryStats.map((c) => {
          const Icon = c.icon
          return (
            <div key={c.label} onClick={() => navigate(c.link)} className="card p-4 cursor-pointer hover:shadow-md transition-shadow flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl ${c.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-gray-900">{loading ? '—' : (c.value ?? '—')}</p>
                <p className="text-xs text-gray-400">{c.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Fee chart + Recent Activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-bold text-gray-900">Fee Collection Overview</h2>
              <p className="text-xs text-gray-400 mt-0.5">Total collected vs pending this year</p>
            </div>
            <button onClick={() => navigate('/fees/analytics')} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
              View analytics
            </button>
          </div>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 mb-1">Total Demand</p>
                  <p className="text-lg font-extrabold text-gray-900">
                    {feeSummary?.totalDemand ? `₹${(feeSummary.totalDemand / 100000).toFixed(1)}L` : '—'}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-400 mb-1">Collected</p>
                  <p className="text-lg font-extrabold text-blue-700">
                    {feeSummary?.totalCollected ? `₹${(feeSummary.totalCollected / 100000).toFixed(1)}L` : '—'}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4">
                  <p className="text-xs text-orange-400 mb-1">Pending</p>
                  <p className="text-lg font-extrabold text-orange-600">
                    {feeSummary?.totalPending ? `₹${(feeSummary.totalPending / 100000).toFixed(1)}L` : '—'}
                  </p>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Collection progress</span>
                  <span className="text-xs font-bold text-blue-600">
                    {feeSummary?.collectionPercent != null
                      ? `${Number(feeSummary.collectionPercent).toFixed(1)}%`
                      : feeSummary?.totalDemand
                        ? `${((feeSummary.totalCollected / feeSummary.totalDemand) * 100).toFixed(1)}%`
                        : '—'}
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(
                        feeSummary?.collectionPercent ??
                        (feeSummary?.totalDemand ? (feeSummary.totalCollected / feeSummary.totalDemand) * 100 : 0),
                        100
                      )}%`
                    }}
                  />
                </div>
              </div>
              {feeSummary?.statusCounts && (
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(feeSummary.statusCounts).map(([status, count]) => (
                    <span key={status} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                      {status}: {count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">Recent Activity</h2>
            <button onClick={() => navigate('/notifications')} className="text-xs text-primary-600 hover:text-primary-700 font-medium">View all</button>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-gray-200 mt-1.5 flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-gray-100 rounded w-3/4" />
                    <div className="h-2 bg-gray-100 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : activity.length === 0 ? (
            <div className="empty-state py-8">
              <p className="empty-state-title mt-0">No recent activity yet</p>
              <p className="empty-state-copy">Recent notifications and system events will appear here once your team starts using the admin workflows.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activity.map((a, i) => (
                <div key={a._id || a.id || i} className="flex items-start gap-3">
                  <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${activityColors[i % activityColors.length]}`} />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-700 leading-relaxed">{a.title || a.text || a.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {a.createdAt ? formatShortDate(new Date(a.createdAt)) : a.time || ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
